import { supabase } from '@/lib/supabaseClient'

// Interface for Order items (products)
interface OrderProduct {
  id: string;
  name: string;
  quantity: number;
  price: number;
  image?: string;
  description?: string;
}

// Interface for Order
export interface Order {
  id: string;
  orderid: string;
  customer_id?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  shipping_date?: string;
  shopifyorderid?: string;
  payment_method?: string;
  totalamount?: number;
  discount?: number;
  tax?: number;
  shipping_charge?: number;
  couponsdiscount?: number;
  total?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
  products?: OrderProduct[];
  vendor?: string;
  customerImage?: string; // Additional field for UI purposes
}

// Interface for location sales
export interface LocationSales {
  name: string;
  progress: number;
  count: number;
  amount: number;
  image?: string;
}

// Function to get all orders
export async function getOrders(organizationId: string | null = null) {
  try {
    // If no organizationId provided, get the current user's organization
    if (!organizationId) {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('User not authenticated - returning sample data');
        return { orders: getSampleOrders(), error: null };
      }
      
      // Get the user's organization ID
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !userProfile?.org_id) {
        console.warn('User has no organization - returning sample data');
        return { orders: getSampleOrders(), error: null };
      }
      
      organizationId = userProfile.org_id as string;
    }
    
    // Fetch orders from the database
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching orders:', error);
      return { orders: [], error: error.message };
    }
    
    if (!data || data.length === 0) {
      console.warn('No orders found in database');
      return { orders: [], error: null };
    }
    
    // Process the orders
    const orders = await processOrders(data);
    
    return { orders, error: null };
  } catch (error) {
    console.error('Error in getOrders:', error);
    return { 
      orders: getSampleOrders(), 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Get order by ID
export async function getOrderById(orderId: string) {
  try {
    console.log('Fetching order by ID:', orderId);
    
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();
    
    if (error) {
      console.error('Error fetching order:', error);
      return { order: null, error: error.message };
    }
    
    if (!data) {
      console.warn('Order not found with ID:', orderId);
      return { order: null, error: 'Order not found' };
    }
    
    console.log('Raw order data:', data);
    console.log('Products field before processing:', data.products);
    
    // Process the order to add customer image and format data
    const orders = await processOrders([data]);
    
    console.log('Processed order with products:', orders[0].products);
    
    return { order: orders[0], error: null };
  } catch (error) {
    console.error('Exception in getOrderById:', error);
    return { 
      order: null, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Create a new order
export async function createOrder(orderData: Partial<Order>) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }
    
    // Get the user's organization ID
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();
    
    if (profileError || !userProfile?.org_id) {
      return { success: false, error: 'User has no organization' };
    }
    
    // Add the organization ID to the order data
    const newOrderData = {
      ...orderData,
      organization_id: userProfile.org_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('orders')
      .insert(newOrderData)
      .select();
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true, order: data ? data[0] : null, error: null };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Update an existing order
export async function updateOrder(orderId: string, orderData: Partial<Order>) {
  try {
    const updateData = {
      ...orderData,
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', orderId)
      .select();
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true, order: data ? data[0] : null, error: null };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Delete an order
export async function deleteOrder(orderId: string) {
  try {
    const { error } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true, error: null };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Function to process orders and add customer images
async function processOrders(orders: any[]): Promise<Order[]> {
  console.log(`[OrderService] processOrders - Starting processing for ${orders.length} orders`);
  const startTime = performance.now();
  
  // Get random profile image for fallback (between user1.jpg and user5.jpg)
  const getRandomUserImage = () => {
    const userNumber = Math.floor(Math.random() * 5) + 1;
    return new URL(`../assets/images/users/user${userNumber}.jpg`, import.meta.url).href;
  };

  const processedOrders = await Promise.all(orders.map(async (order, orderIndex) => {
    console.log(`[OrderService] Processing order ${orderIndex + 1}/${orders.length}, ID: ${order.id}`);
    const orderStartTime = performance.now();
    
    // Assign a random generic image as default
    let customerImage = getRandomUserImage();
    
    // Try to get customer image if customer_id exists
    if (order.customer_id) {
      console.log(`[OrderService] Fetching customer image for customer ID: ${order.customer_id}`);
      const { data: customer } = await supabase
        .from('clients')
        .select('avatar_url')
        .eq('id', order.customer_id)
        .single();
      
      if (customer && customer.avatar_url) {
        // Only use customer avatar if it's a valid URL
        customerImage = customer.avatar_url as string;
        console.log(`[OrderService] Found avatar for customer ${order.customer_id}`);
      } else {
        console.log(`[OrderService] No valid avatar found for customer ${order.customer_id}, using default`);
      }
    } else {
      console.log(`[OrderService] No customer_id for order ${order.id}, using default avatar`);
    }
    
    // Parse products array from string if needed
    let productIds = order.products;
    if (typeof productIds === 'string') {
      try {
        console.log(`[OrderService] Parsing products JSON for order ${order.id}`);
        productIds = JSON.parse(productIds);
        console.log(`[OrderService] Successfully parsed products JSON, found ${productIds.length} products`);
      } catch (e) {
        console.warn(`[OrderService] Failed to parse products JSON for order ${order.id}:`, e);
        productIds = [];
      }
    } else {
      console.log(`[OrderService] Products for order ${order.id} already in correct format, type: ${typeof productIds}`);
    }
    
    console.log(`[OrderService] Product IDs for order ${order.id}:`, productIds);
    
    // First check if the productIds array might already contain complete product objects
    // This can happen if the products are stored as a JSON array of objects
    let products: OrderProduct[] = [];
    
    if (Array.isArray(productIds) && productIds.length > 0) {
      console.log(`[OrderService] Processing ${productIds.length} products for order ${order.id}`);
      
      // Check if the first element is an object with product-like properties
      if (typeof productIds[0] === 'object' && productIds[0] !== null && 'name' in productIds[0]) {
        console.log(`[OrderService] Products already contain complete objects for order ${order.id}`);
        
        // The array contains product objects, not just IDs
        products = productIds.map((product: any, index: number) => {
          console.log(`[OrderService] Mapping product object ${index + 1}/${productIds.length} for order ${order.id}`);
          return {
            id: product.id || `product-${Math.random().toString(36).substring(7)}`,
            name: product.name || product.title || 'Unknown Product',
            quantity: parseInt(product.quantity, 10) || 1,
            price: parseFloat(product.price || product.compare_at_price) || 0,
            image: product.image || product.image_link || '',
          };
        });
        
        console.log(`[OrderService] Mapped ${products.length} complete product objects for order ${order.id}`);
      } else {
        // The array contains product IDs, we need to fetch the details
        console.log(`[OrderService] Fetching product details for ${productIds.length} IDs in order ${order.id}`);
        
        // First try to get product quantities from order_items if available
        let orderItems: { product_id: string, quantity: number }[] = [];
        // No need to fetch order_items as the table doesn't exist
        // Instead, assume default quantities for each product
        console.log(`[OrderService] Using default quantities for products in order ${order.id}`);
        orderItems = productIds.map(productId => ({
          product_id: String(productId),
          quantity: 1 // Default quantity
        }));
        
        // Fetch products from the products table
        console.log(`[OrderService] Fetching product data from products table for order ${order.id}`);
        const productFetchStart = performance.now();
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('id, title, compare_at_price, image_link, price, description')
          .in('id', productIds);
        
        const productFetchEnd = performance.now();
        
        if (productError) {
          console.error(`[OrderService] Error fetching products for order ${order.id}:`, productError);
        } else if (productData && productData.length > 0) {
          console.log(`[OrderService] Retrieved ${productData.length}/${productIds.length} products in ${(productFetchEnd - productFetchStart).toFixed(2)}ms for order ${order.id}`);
          
          // Map product data to OrderProduct format
          products = productIds.map((productId, index) => {
            console.log(`[OrderService] Processing product ${index + 1}/${productIds.length}, ID: ${productId} for order ${order.id}`);
            
            // Find the product in the fetched data
            const productDetails = productData.find(p => p.id === productId);
            
            // Find quantity if available in order_items
            const orderItem = orderItems.find(item => item.product_id === String(productId));
            const quantity = orderItem ? orderItem.quantity : 1;
            
            if (productDetails) {
              console.log(`[OrderService] Found details for product ${productId}`);
              return {
                id: String(productDetails.id),
                name: String(productDetails.title) || 'Unknown Product',
                // Use price if available, fall back to compare_at_price
                price: parseFloat(String(productDetails.price || productDetails.compare_at_price)) || 0,
                quantity: quantity,
                image: String(productDetails.image_link || ''),
                description: productDetails.description ? String(productDetails.description) : undefined,
              };
            }
            
            // Fallback if product not found
            console.warn(`[OrderService] Product ${productId} not found in database for order ${order.id}`);
            return {
              id: String(productId),
              name: 'Product Not Found',
              price: 0,
              quantity: quantity,
              image: '',
            };
          });
        } else {
          console.warn(`[OrderService] No product data found for order ${order.id} with IDs:`, productIds);
        }
      }
    } else {
      console.warn(`[OrderService] No valid products array for order ${order.id}, type: ${typeof productIds}, isArray: ${Array.isArray(productIds)}, length: ${Array.isArray(productIds) ? productIds.length : 0}`);
    }
    
    // If we couldn't get any products, use fallback or generate samples
    if (products.length === 0) {
      console.warn(`[OrderService] No products found for order ${order.id}, using fallback`);
      
      if (process.env.NODE_ENV === 'development') {
        // Only generate sample products in development mode
        const generatedProductCount = Math.floor(Math.random() * 3) + 1;
        console.log(`[OrderService] Generating ${generatedProductCount} sample products for order ${order.id}`);
        
        products = Array.from({ length: generatedProductCount }, (_, i) => ({
          id: `generated-${i + 1}`,
          name: `Generated Product ${i + 1}`,
          quantity: Math.floor(Math.random() * 3) + 1,
          price: parseFloat((Math.random() * 100 + 10).toFixed(2)),
          image: new URL(`../assets/images/products/product${(i % 5) + 1}.jpg`, import.meta.url).href
        }));
        
        console.log(`[OrderService] Created ${generatedProductCount} sample products for order ${order.id}`);
      }
    }
    
    const orderEndTime = performance.now();
    console.log(`[OrderService] Completed processing order ${order.id} with ${products.length} products in ${(orderEndTime - orderStartTime).toFixed(2)}ms`);
    
    return {
      ...order,
      customerImage,
      products
    };
  }));
  
  const endTime = performance.now();
  console.log(`[OrderService] processOrders completed in ${(endTime - startTime).toFixed(2)}ms, processed ${processedOrders.length} orders`);
  
  return processedOrders;
}

// Sample orders for fallback
function getSampleOrders(): Order[] {
  // Import images directly 
  const user1Image = new URL(`../assets/images/users/user1.jpg`, import.meta.url).href;
  const user2Image = new URL(`../assets/images/users/user2.jpg`, import.meta.url).href;
  const user3Image = new URL(`../assets/images/users/user3.jpg`, import.meta.url).href;

  return [
    {
      id: "1",
      orderid: "#JAN-2345",
      name: "Sarah Johnson",
      email: "sarah@example.com",
      phone: "123-456-7890",
      products: [
        { id: "1", name: "Product 1", quantity: 2, price: 29.99 },
        { id: "2", name: "Product 2", quantity: 1, price: 49.99 }
      ],
      total: 109.97,
      status: "shipped",
      created_at: "2024-06-12T00:00:00Z",
      vendor: "Dennis Matthews",
      customerImage: user1Image
    },
    {
      id: "2",
      orderid: "#JAN-1323",
      name: "Michael Smith",
      email: "michael@example.com",
      phone: "987-654-3210",
      products: [
        { id: "3", name: "Product 3", quantity: 3, price: 19.99 },
        { id: "4", name: "Product 4", quantity: 1, price: 129.99 }
      ],
      total: 189.96,
      status: "processing",
      created_at: "2024-06-11T00:00:00Z",
      vendor: "Laura Perez",
      customerImage: user2Image
    },
    {
      id: "3",
      orderid: "#JAN-5287",
      name: "David Wilson",
      email: "david@example.com",
      phone: "555-123-4567",
      products: [
        { id: "5", name: "Product 5", quantity: 1, price: 299.99 }
      ],
      total: 299.99,
      status: "delivered",
      created_at: "2024-06-10T00:00:00Z",
      vendor: "Robert Chen",
      customerImage: user3Image
    }
  ];
}

// Get sales by locations (countries)
export async function getSalesByLocations(organizationId: string | null = null, period = 'week') {
  console.log(`[OrderService] Getting sales by locations - period: ${period}`);
  try {
    // If no organizationId provided, get the current user's organization
    if (!organizationId) {
      console.log('[OrderService] No organizationId provided, getting current user');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('[OrderService] User not authenticated - returning sample data');
        return { locations: getSampleLocationSales(), error: null };
      }
      
      // Get the user's organization ID
      console.log('[OrderService] Getting user organization ID');
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !userProfile?.org_id) {
        console.warn('[OrderService] User has no organization - returning sample data', profileError);
        return { locations: getSampleLocationSales(), error: null };
      }
      
      organizationId = userProfile.org_id as string;
      console.log(`[OrderService] Using organization ID: ${organizationId}`);
    }
    
    // Calculate date range based on period
    const dateFilter = new Date();
    switch(period) {
      case 'day':
        dateFilter.setDate(dateFilter.getDate() - 1);
        break;
      case 'week':
        dateFilter.setDate(dateFilter.getDate() - 7);
        break;
      case 'month':
        dateFilter.setMonth(dateFilter.getMonth() - 1);
        break;
      case 'year':
        dateFilter.setFullYear(dateFilter.getFullYear() - 1);
        break;
      default:
        dateFilter.setDate(dateFilter.getDate() - 7); // Default to week
    }
    
    // Format date for SQL
    const dateStr = dateFilter.toISOString();
    console.log(`[OrderService] Filtering orders from date: ${dateStr}`);
    
    // Fetch orders with country data
    console.log('[OrderService] Fetching orders with country data');
    const { data: orders, error: orderError } = await supabase
      .from('orders')
      .select('id, country, totalamount')
      .eq('organization_id', organizationId)
      .gt('created_at', dateStr)
      .not('country', 'is', null);
    
    if (orderError) {
      console.error('[OrderService] Error fetching orders with country data:', orderError);
      return { locations: getSampleLocationSales(), error: orderError.message };
    }
    
    console.log(`[OrderService] Retrieved ${orders?.length || 0} orders with country data`);
    
    if (!orders || orders.length === 0) {
      console.warn('[OrderService] No orders with country data found, returning sample data');
      return { locations: getSampleLocationSales(), error: null };
    }
    
    // Count and aggregate orders by country
    const countryCounts: Record<string, {count: number, amount: number}> = {};
    let totalOrders = 0;
    let totalAmount = 0;
    
    orders.forEach(order => {
      // Normalize country name to handle case differences
      const countryName = order.country ? String(order.country).trim() : 'Unknown';
      const amount = typeof order.totalamount === 'number' ? order.totalamount : 0;
      
      if (!countryCounts[countryName]) {
        countryCounts[countryName] = { count: 0, amount: 0 };
      }
      
      countryCounts[countryName].count++;
      countryCounts[countryName].amount += amount;
      totalOrders++;
      totalAmount += amount;
    });
    
    console.log('[OrderService] Country order counts:', countryCounts);
    
    // Convert to array and calculate percentages
    const countryPercentages = Object.entries(countryCounts)
      .map(([name, data]) => ({
        name,
        count: data.count,
        amount: data.amount,
        // Calculate percentage based on count
        progress: Math.round((data.count / totalOrders) * 100),
        // Add flag image if available
        image: getFlagImage(name)
      }))
      .sort((a, b) => b.progress - a.progress)
      .slice(0, 6); // Get top 6 countries
    
    console.log('[OrderService] Processed location sales data:', countryPercentages);
    
    return { locations: countryPercentages, error: null };
  } catch (error) {
    console.error('[OrderService] Exception in getSalesByLocations:', error);
    return { 
      locations: getSampleLocationSales(), 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

// Helper function to get flag image for a country
function getFlagImage(countryName: string): string {
  // Map of common country names to flag images
  const flagMap: Record<string, string> = {
    'usa': 'usa.svg',
    'united states': 'usa.svg',
    'us': 'usa.svg',
    'אצות הברית': 'usa.svg',
    'ארצות הברית': 'usa.svg',
    'ארה״ב': 'usa.svg',
    'ארהב': 'usa.svg',
    
    'uk': 'uk.svg',
    'united kingdom': 'uk.svg',
    'england': 'uk.svg',
    'great britain': 'uk.svg',
    'בריטניה': 'uk.svg',
    'אנגליה': 'uk.svg',
    'אנגלייה': 'uk.svg',
    
    'canada': 'canada.svg',
    'קנדה': 'canada.svg',
    
    'germany': 'germany.svg',
    'גרמניה': 'germany.svg',
    
    'spain': 'spain.svg',
    'ספרד': 'spain.svg',
    
    'portugal': 'portugal.svg',
    'פורטוגל': 'portugal.svg',
    
    'france': 'france.svg',
    'צרפת': 'france.svg',
    
    'italy': 'italy.svg',
    'איטליה': 'italy.svg',
    
    'israel': 'israel.svg',
    'ישראל': 'israel.svg',
    
    'australia': 'australia.svg',
    'אוסטרליה': 'australia.svg',
    
    'japan': 'japan.svg',
    'יפן': 'japan.svg',
    
    'china': 'china.svg',
    'סין': 'china.svg',
    
    'india': 'india.svg',
    'הודו': 'india.svg',
    
    'brazil': 'brazil.svg',
    'ברזיל': 'brazil.svg',
    
    'mexico': 'mexico.svg',
    'מקסיקו': 'mexico.svg'
  };
  
  // Normalize country name (lowercase)
  const normalizedName = countryName.toLowerCase();
  
  // Find matching flag filename
  return flagMap[normalizedName] || 'israel.svg'; // Default to Israel flag if not found
}

// Sample location sales for fallback
function getSampleLocationSales(): LocationSales[] {
  return [
    {
      name: "United States",
      image: new URL('../assets/images/flags/usa.svg', import.meta.url).href,
      progress: 35,
      count: 85,
      amount: 4250
    },
    {
      name: "Germany",
      image: new URL('../assets/images/flags/germany.svg', import.meta.url).href,
      progress: 25,
      count: 35,
      amount: 1750
    },
    {
      name: "United Kingdom",
      image: new URL('../assets/images/flags/uk.svg', import.meta.url).href,
      progress: 15,
      count: 20,
      amount: 1000
    },
    {
      name: "Canada",
      image: new URL('../assets/images/flags/canada.svg', import.meta.url).href,
      progress: 10,
      count: 15,
      amount: 750
    },
    {
      name: "Israel",
      image: new URL('../assets/images/flags/israel.svg', import.meta.url).href,
      progress: 8,
      count: 10,
      amount: 500
    },
    {
      name: "Spain",
      image: new URL('../assets/images/flags/spain.svg', import.meta.url).href,
      progress: 7,
      count: 8,
      amount: 400
    }
  ];
}

// Get aggregated sales data for dashboard charts
export async function getTotalSalesData(organizationId: string | null = null, period = 'monthly') {
  console.log(`[OrderService] Getting total sales data - period: ${period}`);
  try {
    // If no organizationId provided, get the current user's organization
    if (!organizationId) {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('[OrderService] User not authenticated - returning sample data');
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: null 
        };
      }
      
      // Get the user's organization ID
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();
      
      if (profileError || !userProfile?.org_id) {
        console.warn('[OrderService] User has no organization - returning sample data');
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: null 
        };
      }
      
      organizationId = userProfile.org_id as string;
    }
    
    // Get current date info
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const previousYear = currentYear - 1;
    const currentMonth = currentDate.getMonth(); // 0-11
    
    // For weekly view, we need to calculate date ranges differently
    if (period === 'weekly') {
      const currentDay = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      
      // Calculate the start of current week (Sunday)
      const startOfCurrentWeek = new Date(currentDate);
      startOfCurrentWeek.setDate(currentDate.getDate() - currentDay);
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      
      // Calculate the end of current week (Saturday)
      const endOfCurrentWeek = new Date(startOfCurrentWeek);
      endOfCurrentWeek.setDate(startOfCurrentWeek.getDate() + 6);
      endOfCurrentWeek.setHours(23, 59, 59, 999);
      
      // Calculate the same week from last year
      const startOfPreviousYearWeek = new Date(startOfCurrentWeek);
      startOfPreviousYearWeek.setFullYear(currentYear - 1);
      
      const endOfPreviousYearWeek = new Date(endOfCurrentWeek);
      endOfPreviousYearWeek.setFullYear(currentYear - 1);
      
      console.log(`[OrderService] Weekly date ranges:
        Current: ${startOfCurrentWeek.toISOString()} to ${endOfCurrentWeek.toISOString()}
        Previous: ${startOfPreviousYearWeek.toISOString()} to ${endOfPreviousYearWeek.toISOString()}`);
      
      // Fetch current week orders
      const { data: currentWeekOrders, error: currentWeekError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', startOfCurrentWeek.toISOString())
        .lte('created_at', endOfCurrentWeek.toISOString());
      
      if (currentWeekError) {
        console.error('[OrderService] Error fetching current week orders:', currentWeekError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: currentWeekError.message 
        };
      }
      
      // Fetch previous year's same week orders
      const { data: previousWeekOrders, error: previousWeekError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', startOfPreviousYearWeek.toISOString())
        .lte('created_at', endOfPreviousYearWeek.toISOString());
      
      if (previousWeekError) {
        console.error('[OrderService] Error fetching previous week orders:', previousWeekError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: previousWeekError.message 
        };
      }
      
      console.log(`[OrderService] Retrieved ${currentWeekOrders?.length || 0} current week orders and ${previousWeekOrders?.length || 0} previous week orders`);
      
      // Process data
      const currentWeekData = processOrdersForChart(currentWeekOrders || [], period);
      const previousWeekData = processOrdersForChart(previousWeekOrders || [], period);
      
      return {
        currentYear: currentWeekData,
        previousYear: previousWeekData,
        error: null
      };
    } else if (period === 'monthly') {
      // Calculate the start of current month
      const startOfCurrentMonth = new Date(currentYear, currentMonth, 1);
      startOfCurrentMonth.setHours(0, 0, 0, 0);
      
      // Calculate the end of current month
      const endOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0);
      endOfCurrentMonth.setHours(23, 59, 59, 999);
      
      // Calculate the same month from last year
      const startOfPreviousYearMonth = new Date(startOfCurrentMonth);
      startOfPreviousYearMonth.setFullYear(currentYear - 1);
      
      const endOfPreviousYearMonth = new Date(endOfCurrentMonth);
      endOfPreviousYearMonth.setFullYear(currentYear - 1);
      
      console.log(`[OrderService] Monthly date ranges:
        Current: ${startOfCurrentMonth.toISOString()} to ${endOfCurrentMonth.toISOString()}
        Previous: ${startOfPreviousYearMonth.toISOString()} to ${endOfPreviousYearMonth.toISOString()}`);
      
      // Fetch current month orders
      const { data: currentMonthOrders, error: currentMonthError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', startOfCurrentMonth.toISOString())
        .lte('created_at', endOfCurrentMonth.toISOString());
      
      if (currentMonthError) {
        console.error('[OrderService] Error fetching current month orders:', currentMonthError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: currentMonthError.message 
        };
      }
      
      // Fetch previous year's same month orders
      const { data: previousMonthOrders, error: previousMonthError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', startOfPreviousYearMonth.toISOString())
        .lte('created_at', endOfPreviousYearMonth.toISOString());
      
      if (previousMonthError) {
        console.error('[OrderService] Error fetching previous month orders:', previousMonthError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: previousMonthError.message 
        };
      }
      
      console.log(`[OrderService] Retrieved ${currentMonthOrders?.length || 0} current month orders and ${previousMonthOrders?.length || 0} previous month orders`);
      
      // Process data
      const currentMonthData = processOrdersForChart(currentMonthOrders || [], period);
      const previousMonthData = processOrdersForChart(previousMonthOrders || [], period);
      
      return {
        currentYear: currentMonthData,
        previousYear: previousMonthData,
        error: null
      };
    } else {
      // Other periods (yearly, etc.)
      // Fetch current year orders
      const { data: currentYearOrders, error: currentYearError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', `${currentYear}-01-01`)
        .lte('created_at', `${currentYear}-12-31`);
      
      if (currentYearError) {
        console.error('[OrderService] Error fetching current year orders:', currentYearError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: currentYearError.message 
        };
      }
      
      // Fetch previous year orders
      const { data: previousYearOrders, error: previousYearError } = await supabase
        .from('orders')
        .select('created_at, totalamount, total')
        .eq('organization_id', organizationId)
        .gte('created_at', `${previousYear}-01-01`)
        .lte('created_at', `${previousYear}-12-31`);
      
      if (previousYearError) {
        console.error('[OrderService] Error fetching previous year orders:', previousYearError);
        return { 
          currentYear: getSampleSalesData(period, 'current'),
          previousYear: getSampleSalesData(period, 'previous'),
          error: previousYearError.message 
        };
      }
      
      console.log(`[OrderService] Retrieved ${currentYearOrders?.length || 0} current year orders and ${previousYearOrders?.length || 0} previous year orders`);
      
      // Process data based on period
      const currentYearData = processOrdersForChart(currentYearOrders || [], period);
      const previousYearData = processOrdersForChart(previousYearOrders || [], period);
      
      return {
        currentYear: currentYearData,
        previousYear: previousYearData,
        error: null
      };
    }
  } catch (error) {
    console.error('[OrderService] Exception in getTotalSalesData:', error);
    return { 
      currentYear: getSampleSalesData(period, 'current'),
      previousYear: getSampleSalesData(period, 'previous'),
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// Process orders for chart display
function processOrdersForChart(orders: any[], period: string): number[] {
  if (period === 'monthly') {
    // Get the current month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Get days in current month (28-31 depending on month)
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    // Initialize array with days in the month (e.g. 1-31 for most months)
    const dailyData = Array(daysInMonth).fill(0);
    
    orders.forEach(order => {
      if (!order.created_at) return;
      
      const orderDate = new Date(order.created_at);
      const dayOfMonth = orderDate.getDate(); // 1-31
      
      // Use total or totalamount field, whichever is available
      const amount = order.total !== undefined && order.total !== null ? 
        parseFloat(order.total) : 
        (order.totalamount !== undefined && order.totalamount !== null ? 
          parseFloat(order.totalamount) : 0);
      
      // Add to the appropriate day (adjusting for 0-based index)
      dailyData[dayOfMonth - 1] += amount;
    });
    
    // Group data by days based on how many we want to display
    // If we have 31 days but only want to show 12 data points, group them
    const displayPoints = 12; // Number of data points to display
    const groupedData = [];
    
    // If month has more days than display points, group them
    if (daysInMonth > displayPoints) {
      const groupSize = Math.ceil(daysInMonth / displayPoints);
      
      for (let i = 0; i < displayPoints; i++) {
        const startIdx = i * groupSize;
        const endIdx = Math.min(startIdx + groupSize, daysInMonth);
        let groupSum = 0;
        
        for (let j = startIdx; j < endIdx; j++) {
          groupSum += dailyData[j];
        }
        
        groupedData.push(groupSum);
      }
    } else {
      // Use daily data as is
      groupedData.push(...dailyData);
    }
    
    // Convert to thousands and round to integers for display
    return groupedData.map(amount => Math.round(amount / 1000));
  } else if (period === 'weekly') {
    // Initialize array with 7 days (Sun-Sat)
    const weeklyData = Array(7).fill(0);
    
    // Get current date and calculate the start of the week (Sunday)
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Calculate the date for the previous Sunday (start of week)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay);
    startOfWeek.setHours(0, 0, 0, 0);
    
    console.log(`[OrderService] Processing weekly data from ${startOfWeek.toISOString()}`);
    
    orders.forEach(order => {
      if (!order.created_at) return;
      
      const orderDate = new Date(order.created_at);
      
      // Only include orders from the current week
      if (orderDate < startOfWeek) return;
      
      const dayOfWeek = orderDate.getDay(); // 0-6, where 0 is Sunday
      
      // Use total or totalamount field, whichever is available
      const amount = order.total !== undefined && order.total !== null ? 
        parseFloat(order.total) : 
        (order.totalamount !== undefined && order.totalamount !== null ? 
          parseFloat(order.totalamount) : 0);
      
      // Add to the appropriate day
      weeklyData[dayOfWeek] += amount;
    });
    
    // Convert to thousands and round to integers for display
    return weeklyData.map(amount => Math.round(amount / 1000));
  } else if (period === 'yearly') {
    // Yearly data (by quarters or months)
    return Array(7).fill(0); // Not implemented for yearly view
  } else {
    // Daily data 
    return Array(5).fill(0); // Not implemented for daily view
  }
}

// Generate sample sales data for fallback
function getSampleSalesData(period: string, year: 'current' | 'previous'): number[] {
  if (period === 'monthly') {
    return year === 'current' 
      ? [35, 50, 55, 60, 50, 60, 55, 60, 78, 40, 95, 80]
      : [70, 50, 40, 40, 62, 52, 80, 40, 60, 53, 70, 70];
  } else if (period === 'weekly') {
    return year === 'current'
      ? [10, 20, 30, 40, 50, 60, 70]
      : [15, 25, 35, 45, 55, 65, 75];
  } else if (period === 'yearly') {
    return year === 'current'
      ? [90, 99, 80, 70, 99, 75, 95]
      : [99, 70, 85, 90, 75, 90, 80];
  } else { // daily
    return year === 'current'
      ? [5, 10, 15, 20, 25]
      : [3, 8, 12, 18, 22];
  }
} 